import { ClockCircleOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import StickerPopover, { StickerPopoverProps } from 'components/Sticker/StickerPopover';
import { useGetAllSticker, useStickers } from 'hooks/business/im/sticker';
import { useGetRecentUsedStickers } from 'hooks/business/im/sticker/recent';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { useRef, useMemo, useEffect } from 'react';
import { T } from '@tolgee/react';
import { scheduleIdle } from 'utils/scheduleIdle';

export interface ToolbarStikerPopoverProps
  extends Omit<StickerPopoverProps<Conversation.Sticker>, 'stickers'> {
  region: General.CaselessRegion;
  platform: General.PlatformUpperCaseTunnel;
}

type SourceNameLng = keyof Required<Required<Conversation.Sticker>['name']>;
const sourceNameLngMap: Record<General.SystemLanguage, SourceNameLng> = {
  'en-US': 'en',
  'zh-CN': 'zh-CN',
  id: 'id',
  th: 'th',
  tl: 'en',
  es: 'en',
  vi: 'vi',
  pt: 'en',
};

const RecentTabIcon = styled(ClockCircleOutlined)`
  svg {
    font-size: 0.8em;
    color: rgba(0, 0, 0, 0.5);
  }
`;
const RecentTitle = styled.h6`
  margin: 0;
  padding: 5px 10px;
  font-weight: normal;
  font-size: 12px;
  color: #515359;
`;
const transform = (
  it: Conversation.Sticker,
  lng: General.SystemLanguage,
  isRecent = false,
): StickerPopoverProps<Conversation.Sticker>['stickers'][number] => {
  const name = it?.name?.[sourceNameLngMap[lng]];
  const pack = isRecent ? '@@local__recent' : it.pid;
  return {
    id: `${pack}_${it?.pid}_${it?.sid}`,
    pack,
    packLabel: isRecent ? <RecentTabIcon /> : undefined,
    packTitle: isRecent ? (
      <RecentTitle>
        <T keyName="translation.常用表情" />
      </RecentTitle>
    ) : undefined,
    imageUrl: it.ossStickerAddress,
    name,
    raw: it,
  };
};
const ToolbarStickerPopover = ({ region, platform, ...otherProps }: ToolbarStikerPopoverProps) => {
  const stickers = useStickers({
    region,
    channel: platform,
    filter: 'enabled',
  });
  const recentStickers = useGetRecentUsedStickers();
  const getAllSticker = useGetAllSticker();

  const refsCurrent = {
    getAllSticker,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    if (stickers || !region || !platform) {
      return;
    }

    const {
      current: { getAllSticker },
    } = refs;

    scheduleIdle(() => {
      getAllSticker();
    });
  }, [region, platform, stickers]);

  const lng = useSystemLanguageValue();

  const normalStickers = useMemo(() => {
    return stickers ? stickers.map((it) => transform(it, lng)) : [];
  }, [lng, stickers]);

  const allStickers = useMemo(() => {
    return recentStickers.map((it) => transform(it, lng, true)).concat(...normalStickers);
  }, [lng, normalStickers, recentStickers]);

  return (
    <StickerPopover<Conversation.Sticker>
      key={`${platform}@${region}`}
      stickers={allStickers}
      {...otherProps}
    />
  );
};

export default ToolbarStickerPopover;
